import permanentShopPageQuery from 'queries/permanentShopPage.graphql';
import {
  PageProps,
  QueryPermanentShopPageOutput,
  QueryPermanentShopPageArgs,
  PermanentShopEnum,
} from 'types/types';
import { setGTMPageName } from '../../util/tag-manager';
import {
  fetchGraphQl,
  parseQueryAST,
  isAbortError,
  isNotFound,
} from '../../util/graphql';
import { shopPageProps } from 'components/_pages/shop/utils';
import ShopPage from 'components/_pages/shop';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { ShopPageInterface } from 'components/_pages/shop/types';
import { useCustomerContext, useStaffPreview } from 'hooks/customer';
import useErrorNotificationHandler from 'hooks/handle-errors';
import { useLazyQuery } from '@apollo/client';
import { useEffect } from 'react';
import Loading from 'components/loading';
import config from '../../util/load-config';
import NotFoundPage from 'pages/404';
import { prepMetaTags } from '../../util/seo';
import { PageNamesEnum } from 'types/third-party/gtm';
import { canonicalUrl } from '../../util/url';
import { logError } from '../../util/error-logger';
import { cacheControl } from 'constants/cache-control';
import { logInvalidJSON } from '../../util/log-invalid-json';
import findPermanentShopIndex from '../../util/find-permanent-shop-index';

setGTMPageName(PageNamesEnum.permanentShopsPage);

interface ShopPageProps {
  shopPage?: ShopPageInterface;
  shopId: string;
}

type ShopPageWrapperProps = Partial<ShopPageProps>;

const ShopPageContent = ({
  shopPage: shopPagePageSSR,
  shopId,
}: ShopPageProps) => {
  const context = useCustomerContext();
  const mountErrors = useErrorNotificationHandler();

  const [previewShopPage, { loading, error, data }] = useLazyQuery<
    QueryPermanentShopPageOutput,
    QueryPermanentShopPageArgs
  >(permanentShopPageQuery, {
    context,
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  const hasPermanentProducts =
    data && data.permanentShopProducts && data.permanentShopProducts.length > 0;
  const shopPage = hasPermanentProducts ? shopPageProps(data) : shopPagePageSSR;

  useStaffPreview(({ date }) =>
    previewShopPage({
      variables: {
        date,
        shopId,
        previewShopId: shopId,
      },
    })
  );

  useEffect(() => {
    error && mountErrors([new Error('Preview failed')]);
  }, [error, mountErrors]);

  if (!shopPage) {
    return <NotFoundPage />;
  }

  return (
    <Loading iconTop={200} isLoading={loading} loadingText="Preview loading...">
      <ShopPage {...shopPage} />
    </Loading>
  );
};

const ShopPageWrapper = ({ shopId, ...restProps }: ShopPageWrapperProps) => {
  if (!shopId) return <NotFoundPage />;
  return <ShopPageContent shopId={shopId} {...restProps} />;
};

export async function getServerSideProps({
  query: { id },
  res,
  resolvedUrl,
}: GetServerSidePropsContext): Promise<
  GetServerSidePropsResult<ShopPageWrapperProps & PageProps>
> {
  const canonical = canonicalUrl(resolvedUrl);

  const shopId = typeof id === 'string' ? id : '';

  try {
    if (!shopId) {
      res.statusCode = 404;
      res.setHeader('cache-control', cacheControl.temporary);

      // return unknown shop props
      return { props: {} };
    }

    const [query, operationName] = await parseQueryAST(
      permanentShopPageQuery,
      'permanentShopPage'
    );

    const { data, errors } = await fetchGraphQl<
      QueryPermanentShopPageOutput,
      QueryPermanentShopPageArgs
    >({
      operationName,
      query,
      variables: { shopId, previewShopId: shopId },
      logInvalidJSON,
    });

    // only fail if there is no data
    if (!data || !data.permanentShopProducts) {
      res.setHeader('cache-control', cacheControl.temporary);

      // log error and set status based on error and data
      if (errors && errors.length > 0) {
        const [firstError] = errors;
        logError(firstError.message, `shop.query.${shopId}`);

        if (isNotFound(firstError)) {
          res.statusCode = 404;
          // return empty props
          return { props: { shopId } };
        }
        res.statusCode = 500;
      } else {
        res.statusCode = 404;
        logError(
          `No data returned for the permanentShop: "${shopId}"`,
          `shop.query.${id}`
        );
      }

      // return empty props so we can render a failure page
      return { props: { shopId } };
    }

    // go ahead
    res.setHeader('cache-control', cacheControl.dynamic);

    const [headerTaglineTitle, headerTaglineText] = [
      config.headerTaglines[shopId]?.header,
      config.headerTaglines[shopId]?.text,
    ];

    const meta = config.seo.meta.pages[`shops/${shopId}`];
    const searchIndexName = findPermanentShopIndex(shopId);

    const theme =
      data.permanentShop.id === PermanentShopEnum.EVERYDAY_ESSENTIALS
        ? 'everydayessentials'
        : data.permanentShop.id === PermanentShopEnum.EXTRA_TIME_DEALS
        ? 'extratimedeals'
        : undefined;

    return {
      props: {
        shopPage: shopPageProps(data),
        shopId,
        meta: {
          theme,
          messages: data.messages || null,
        },
        layout: {
          selectedTopTab: data.permanentShop.id,
          hideClock: true,
          hasScrollButton: true,
          hasAppDownloadWidget: true,
          moreProductsFooter: { enabled: true },
          searchIndexName,
          ...(meta.pageHeader || meta.pageDescription
            ? {
                seo: {
                  ...(meta.pageHeader
                    ? { header: { title: meta.pageHeader } }
                    : {}),
                  ...(meta.pageDescription
                    ? { description: meta.pageDescription }
                    : {}),
                },
              }
            : {}),
          ...(headerTaglineTitle ? { headerTaglineTitle } : {}),
          ...(headerTaglineText ? { headerTaglineText } : {}),
          ...(meta
            ? {
                metaTags: {
                  ...prepMetaTags(meta),
                  ...(canonical ? { canonical } : {}),
                },
              }
            : { ...(canonical ? { canonical } : {}) }),
        },
      },
    };
  } catch (e) {
    res.statusCode = isAbortError(e) ? 503 : 500;
    res.setHeader('cache-control', cacheControl.temporary);

    logError(e, `shop.query.${id}`);

    // return empty props so we can render a failure page
    return { props: {} };
  }
}

export default ShopPageWrapper;
